import { IconExternalLink, IconMoon } from '@tabler/icons-react';
import { Tooltip } from '@mantine/core';
import { Anchor, Box, Text } from '@mantine/core';
import { Handle } from '@xyflow/react';
import { memo, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import styles from './DefaultNode.module.css';

import FlowStepIcon from './FlowStepIcon';

const DAY_LETTERS = {
  Monday: 'M', Tuesday: 'T', Wednesday: 'W', Thursday: 'T', Friday: 'F', Saturday: 'S', Sunday: 'S',
};

const DefaultNode = memo(({ data, onNodeSelect, isConnectable }) => {
  const [error, setError] = useState();
  const fetch = useFetch();
  const { domain } = useSelector((state) => state.domain);
  const details = [];
  const dayConfigs = [];
  let observeQuietHours = false;

  const getDetailForSectionStart = (name) => {
    return (
      <>
        {name}
        <hr style={{ margin: '0pt' }} />
      </>
    );
  };

  const getDetailForNumberOrString = (config, value) => {
    if (value === undefined || value === null) {
      return value;
    }
    let detail = value;
    detail = config.detailsPrefix ? `${config.detailsPrefix} ${detail}` : detail;
    detail = config.detailsSuffix ? `${detail} ${config.detailsSuffix}` : detail;
    return detail;
  };

  const getDetailForCheckbox = (name, value) => {
    if ([undefined, true].indexOf(value)) {
      return name;
    }
  };

  // Normalize legacy wait nodes that have no waitMode (derived, not mutated)
  const values = data.custom.key === 'wait'
    ? { waitMode: 'static', ...data.custom.values }
    : data.custom.values;

  for (const config of data.custom.config) {
    // Collect day-of-week checkboxes separately
    if (config.field && config.field.startsWith('runOn')) {
      const dayName = config.field.replace('runOn', '');
      dayConfigs.push({ dayName, enabled: !!values[config.field] });
      continue;
    }
    // Skip the "Send on" section header
    if (config.type === 'sectionStart' && config.name === 'Send on') {
      continue;
    }
    // Skip the empty section separator before observeQuietHours
    if (config.type === 'sectionStart' && config.name === '') {
      continue;
    }
    // Collect observeQuietHours separately
    if (config.field === 'observeQuietHours') {
      observeQuietHours = !!values[config.field];
      continue;
    }

    if (config.showWhen) {
      const currentValue = values[config.showWhen.field];
      if (currentValue !== config.showWhen.value) {
        continue;
      }
    }

    let detail = '';
    const name = (config.overview || {}).name || config.name;
    const value = values[config.field];

    switch (config.type) {
      case 'select':
        break;
      case 'sectionStart':
        detail = getDetailForSectionStart(name);
        break;
      case 'number':
        detail = getDetailForNumberOrString(config, value);
        break;
      case 'string':
        detail = getDetailForNumberOrString(config, value);
        break;
      case 'checkbox':
        detail = getDetailForCheckbox(name, value);
        break;
      default:
        detail = null;
    }
    if (detail) {
      details.push(
        <div key={data.custom.config.indexOf(config)}>
          <small>{detail}</small>
        </div>
      );
    }
  }
  const [previewData, setPreviewData] = useState('');
  const [elementName, setElementName] = useState();
  const [pushData, setPushData] = useState(null);
  const [pushPreviewPlatform, setPushPreviewPlatform] = useState(null);

  useEffect(() => {
    const typeMap = {
      sendEmail: 'emails',
      sendTextMessage: 'textMessages',
      sendPushNotification: 'pushNotifications',
      sendViberMessage: 'viberMessages',
      sendWhatsappMessage: 'whatsappMessages',
      sendFacebookAd: 'facebookAds',
      sendWebhook: 'webhooks',
      sendGoogleAd: 'googleAds',
      sendInboxMessage: 'inboxMessages',
    };
    const type = typeMap[data.custom.key] || '';
    if (!type || !data.custom.values.id) return;
    fetch(`/api/admin/accounts/${domain.accountId}/domains/${domain.id}/${type}/${data.custom.values.id}`)
      .then((data) => {
        if (type === 'pushNotifications') {
          const pn = data.pushNotification;
          if (pn) {
            setPushData(pn);
            const firstPlatform = pn.platforms?.find((p) => p !== 'appInbox') || (pn.platforms?.includes('appInbox') ? 'appInbox' : null);
            setPushPreviewPlatform(firstPlatform);
          }
        }

        const previewStyles = {
          emails: `<html><head><style>body{margin:0;overflow:hidden}</style></head><body><div style="zoom: 20%">${data.email?.html || null}</div></body></html>`,
          pushNotifications: '__push__', // sentinel — rendered via React, not iframe
          textMessages: data.textMessage?.message
            ? `<div style="max-width: 70%; zoom: 75%; background-color: #34b7f1;color: #fff;padding: 10px 15px; border-radius: 0px 20px 20px 20px; box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);font-size: 14px;line-height: 1.4;margin: 5px 0; word-wrap: break-word; align-self: flex-end;">${data.textMessage.message}</div>`
            : null,
          viberMessages: data.viberMessage?.message
            ? `<div style="max-width: 70%; zoom: 75%; width:auto;background-color: #4e7ef2;color: #fff;padding: 10px 15px; border-radius: 0px 20px 20px 20px; box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);font-size: 14px;line-height: 1.4;margin: 5px 0; word-wrap: break-word; align-self: flex-end;">${data.viberMessage.message}</div>`
            : null,
          whatsappMessages: data.whatsappMessage?.body
            ? `<div style="max-width: 70%; zoom: 75%; width:auto;background-color: #25D366;color: #fff;padding: 10px 15px; border-radius: 0px 20px 20px 20px; box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);font-size: 14px;line-height: 1.4;margin: 5px 0; word-wrap: break-word; align-self: flex-end;">${data.whatsappMessage.body}</div>`
            : null,
          facebookAds: data.facebookAd?.message
            ? `<div style="max-width: 100%; zoom: 75%; width:auto;color: #000;padding: 10px 15px;font-size: 14px;line-height: 1.4;margin: 5px 0; word-wrap: break-word; align-self: flex-end;">${data.facebookAd.message}</div>`
            : null,
          inboxMessages: data.inboxMessage?.html
            ? `<div style="zoom: 20%">${data.inboxMessage.html}</div>`
            : null,
        };

        setPreviewData(previewStyles[type] || null);
        setElementName(data[type.slice(0, -1)].name);
      })
      .catch(setError);
  }, [fetch, domain.accountId, domain.id, data.custom.key, data.custom.values.id]);

  const getTypeFromKey = (key) => {
    const typeMap = {
      sendEmail: 'emails',
      sendTextMessage: 'textMessages',
      sendPushNotification: 'push',
      sendFacebookAd: 'facebook/ads',
      sendViberMessage: 'viber',
      sendWhatsappMessage: 'whatsapp',
      sendGoogleAd: 'google/ads',
      sendWebhook: 'webhooks',
      sendInboxMessage: 'inboxMessages',
    };
    return typeMap[key] || '';
  };

  return (
    <>
      <Handle
        type="target"
        position="top"
        isConnectable={isConnectable}
        style={{ top: '-5px', width: '10px', height: '10px' }}
      />
      <Box ta="center" onClick={onNodeSelect}>
        <Box style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
          <FlowStepIcon icon={data.custom.key} />
          {data.custom.values.id ? (
            <Anchor
              href={`#/accounts/${domain.accountId}/domains/${domain.id}/${getTypeFromKey(data.custom.key)}/${
                data.custom.values.id
              }/details`}
              target="_blank"
              size="10px"
              style={{ wordBreak: 'break-word' }}
            >
              {elementName || 'Unknown'} <IconExternalLink size={12} style={{ verticalAlign: 'middle' }} />
            </Anchor>
          ) : (
            <Text size="10px">{data.label}</Text>
          )}
        </Box>
        {previewData === '__push__' && pushData && pushPreviewPlatform && (
          <Box mt="xs">
            <Box className={styles.platformTabsRow}>
              {pushData.platforms?.map((p) => (
                <Text
                  key={p}
                  size="8px"
                  onClick={(e) => { e.stopPropagation(); setPushPreviewPlatform(p); }}
                  className={styles.platformTab}
                  style={{
                    background: pushPreviewPlatform === p ? 'var(--mantine-color-violet-6)' : 'var(--mantine-color-gray-2)',
                    color: pushPreviewPlatform === p ? '#fff' : 'var(--mantine-color-gray-7)',
                  }}
                >
                  {p === 'appInbox' ? 'inbox' : p}
                </Text>
              ))}
            </Box>
            <Box className={styles.pushPreviewCard}>
              {pushPreviewPlatform === 'appInbox' ? (
                pushData.inboxMessage ? (
                  <>
                    <Text size="8px" fw={700} className={styles.inboxTitle}>{pushData.inboxMessage.title}</Text>
                    {pushData.inboxMessage.html && (
                      <Box className={styles.inboxHtmlContainer}>
                        <iframe
                          title="inbox-preview"
                          className={styles.inboxIframe}
                          srcDoc={pushData.inboxMessage.html}
                          sandbox=""
                        />
                      </Box>
                    )}
                  </>
                ) : (
                  <Text size="8px" c="dimmed">No inbox message</Text>
                )
              ) : (
                (() => {
                  const content = pushData.content?.[pushPreviewPlatform];
                  if (!content) return <Text size="8px" c="dimmed">No content</Text>;
                  return (
                    <>
                      {content.subject && <Text size="8px" fw={700} className={styles.contentSubject}>{content.subject}</Text>}
                      {content.body && <Text size="8px" className={styles.contentBody}>{content.body}</Text>}
                      {content.imageUrl && (
                        <Box
                          className={styles.contentImagePreview}
                          style={{ backgroundImage: `url(${content.imageUrl})` }}
                        />
                      )}
                      {content.btnText && (
                        <Text size="7px" c="violet" fw={600}>{content.btnText}</Text>
                      )}
                    </>
                  );
                })()
              )}
            </Box>
          </Box>
        )}
        {previewData && previewData !== '__push__' && (
          <Box mt="xs" className={styles.genericPreviewWrapper}>
            <iframe
              title={data.custom.key}
              className={styles.genericPreviewIframe}
              srcDoc={previewData}
            />
          </Box>
        )}
        {(dayConfigs.length > 0 || observeQuietHours) && (
          <Box className={styles.daysRow}>
            {dayConfigs.map(({ dayName, enabled }) => (
              <span key={dayName} className={enabled ? styles.dayEnabled : styles.dayDisabled}>
                {DAY_LETTERS[dayName]}
              </span>
            ))}
            {observeQuietHours && (
              <Tooltip label="Wait until end of quiet hours" withArrow>
                <span className={styles.quietHoursIcon}>
                  <IconMoon size={10} />
                </span>
              </Tooltip>
            )}
          </Box>
        )}
        {details.length > 0 && (
          <Text size="xs" c="dimmed" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {details}
          </Text>
        )}
        {error && <ErrorMessage message={error.message} />}
      </Box>
      <Handle
        type="source"
        position="bottom"
        isConnectable={isConnectable}
        style={{ bottom: '-5px', width: '10px', height: '10px' }}
      />
    </>
  );
});

export default DefaultNode;
